import { useCallback, useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import Panel, { PanelHeading, PanelSubheading } from 'components/layout/Panel'
import Page from 'rui/widgets/Page'
import Form from 'components/form'
import Input from 'components/form/Input'
import { required } from 'components/form/validations'
import { signIn } from 'actions/auth'
import Thin from 'components/layout/Thin'
import Spacer from 'components/misc/Spacer'
import { LinkButton } from 'components/layout/buttons'
import { ExternalLink } from 'components/layout/links'
import get from 'lodash/get'
import { LocalizedRichText, LocalizedText, useLocalization } from 'localization'

const SignIn = (props) => {
  const { t } = useLocalization()
  const [statusErrorCode, setStatusError] = useState()
  const [lockCode, setLockCode] = useState()

  const { app } = props

  const int = useRef(null)

  const [throttled, setThrottled] = useState(false)

  const dispatch = useDispatch()

  const handleData = useCallback(
    (data) => {
      if (int.current) {
        window.clearTimeout(int)
        int.current = null
      }

      int.current = window.setTimeout(() => {
        setThrottled(false)
      }, 1000)

      setThrottled(true)

      return dispatch(signIn(data)).catch((e) => {
        const code = get(e, 'response.data.lock_code')
        if (code) {
          setLockCode(code)
        }

        setStatusError(e.response.status)

        throw e
      })
    },
    [dispatch]
  )

  useEffect(() => {
    return () => {
      if (int.current) {
        window.clearTimeout(int)
        int.current = null
      }
    }
  }, [])

  const signUpType = useCallback(() => {
    switch (app) {
      case 'drive':
        return 'Driver'
      case 'send':
      default:
        return 'Sender'
    }
  }, [app])

  // For drive, any time there is a lock code (user locked out)
  // EXCEPT for when they are locked out for inactivity (lock code 0)
  // we DO NOT want to show them the Support Link, instead they need to wait
  // for us to reach out to them.
  const hideSupportLink = lockCode && lockCode !== 0 && app === 'drive'

  return (
    <>
      <Page>
        <Helmet>
          <title>{t('dweb_sign_in_app_sign_in', { app: signUpType() })}</title>
        </Helmet>
        <Thin>
          <Form onData={handleData} id="signin-form">
            <Panel>
              <PanelHeading>
                <LocalizedText translationKey="dweb_sign_in_app_sign_in" params={{ app: signUpType() }} />
              </PanelHeading>
              <PanelSubheading>
                <LocalizedText translationKey="dweb_sign_in_welcome_roadie_sign_email_password_below" />
              </PanelSubheading>

              <Input.Email
                className="fs-block"
                label={t('dweb_forgot_password_email_address')}
                name="email"
                validate={required}
              />
              <Input
                password
                className="fs-block"
                label={t('dweb_common_password')}
                name="password"
                validate={required}
              />
              <Spacer />
              <div style={{ textAlign: 'left' }}>
                <LinkButton id="forgot-password" to="forgot-password">
                  <LocalizedText translationKey="dweb_sign_in_forgot_password" />
                </LinkButton>
              </div>

              <Input.Submit disabled={throttled} value={t('dweb_common_sign_in')} />
              <Spacer height="1.6rem" />
              <p>
                <LocalizedRichText
                  translationKey="dweb_sign_in_new_roadie_link1_link2"
                  params={{
                    link1: (
                      <Link to="sign-up" id="sign-up">
                        <LocalizedText translationKey="dweb_sign_in_sign_up" />
                      </Link>
                    ),
                    link2: (
                      <ExternalLink href="http://driver.roadie.com">
                        <LocalizedText translationKey="dweb_common_learn_more" />
                      </ExternalLink>
                    ),
                  }}
                />
              </p>
              {statusErrorCode && <Spacer height="1.6rem" />}
              {!hideSupportLink && statusErrorCode && (
                <p>
                  <LocalizedRichText
                    translationKey="dweb_sign_in_having_trouble_link1"
                    params={{
                      link1: (
                        <ExternalLink href="https://support.roadie.com/support/s/contactsupport">
                          <LocalizedText translationKey="dweb_sign_in_contact_us" />
                        </ExternalLink>
                      ),
                    }}
                  />
                </p>
              )}
            </Panel>
          </Form>
        </Thin>
      </Page>
    </>
  )
}

export default SignIn
